// App header: home link, connection / upload queue indicator, settings (with accounts: a menu with
// the signed-in user's name, settings and logout).
import type { User } from '../../../shared/types';
import { isAndroidApp } from '../lib/android';
import { plural } from '../lib/format';
import type { OutboxStatus } from '../lib/outbox';
import { navigate } from '../lib/router';
import { Icon } from './Icon';
import { confirmAndLogout } from './Logout';
import { Menu } from './Menu';
import { Link } from './NavGuard';

/** "Ana Novak" -> "AN", "ana" -> "A". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]!, words[words.length - 1]!] : words.slice(0, 1);
  return letters.map((w) => [...w][0]!.toLocaleUpperCase('sl')).join('') || '?';
}

function AccountMenu({ user }: { user: User }) {
  return (
    <Menu
      label={`Račun: ${user.displayName}`}
      heading={user.displayName}
      triggerClassName="account-btn"
      trigger={
        <>
          <span className="account-btn__avatar" aria-hidden="true">
            {initials(user.displayName || user.username)}
          </span>
          <span className="account-btn__name">{user.displayName}</span>
        </>
      }
      items={[
        { label: 'Nastavitve', icon: 'settings', onSelect: () => navigate({ name: 'settings' }) },
        // In the Android app the app itself signs out (its device session, see its settings).
        ...(isAndroidApp() ? [] : [{ label: 'Odjava', icon: 'logout' as const, onSelect: () => void confirmAndLogout(), separator: true }]),
      ]}
    />
  );
}

function pendingText(n: number): string {
  return `${plural(n, ['sprememba čaka', 'spremembi čakata', 'spremembe čakajo', 'sprememb čaka'])} na prenos`;
}

export function SyncIndicator({ status }: { status: OutboxStatus }) {
  let tone: 'offline' | 'syncing' | 'pending' | 'warning' | null = null;
  let label = '';
  let short = '';
  if (!status.online) {
    tone = 'offline';
    label = status.pending > 0 ? `Brez povezave · ${pendingText(status.pending)}` : 'Brez povezave';
    short = 'Brez povezave';
  } else if (status.syncing) {
    tone = 'syncing';
    label = status.pendingUploads > 0 ? 'Pošiljam posnetke …' : 'Shranjujem na strežnik …';
    short = 'Pošiljam …';
  } else if (status.pending > 0) {
    tone = status.lastError ? 'warning' : 'pending';
    label = status.lastError ? `${pendingText(status.pending)}: ${status.lastError}` : pendingText(status.pending);
    short = plural(status.pending, ['čaka', 'čakata', 'čakajo', 'čaka']);
  }
  if (!tone) return null;
  const icon = tone === 'offline' ? 'cloudOff' : tone === 'syncing' ? 'spinner' : tone === 'warning' ? 'alert' : 'clock';
  return (
    <Link to={{ name: 'settings' }} className={`sync sync--${tone}`} title={label} aria-label={`${label}. Odpri nastavitve.`}>
      <Icon name={icon} size={18} />
      <span className="sync__long">{tone === 'pending' || tone === 'warning' ? pendingText(status.pending) : short}</span>
      <span className="sync__short">{short}</span>
    </Link>
  );
}

interface TopBarProps {
  status: OutboxStatus;
  /** Sign-in screens: no navigation. */
  minimal?: boolean;
  inert?: boolean;
  /** The signed-in user (accounts); without one the settings icon is shown. */
  user?: User | null;
}

export function TopBar({ status, minimal, inert, user }: TopBarProps) {
  return (
    <header className="topbar" inert={inert}>
      <div className="topbar__inner">
        <Link to={{ name: 'home' }} className="brand" aria-label="Transkripcija – na začetno stran">
          <span className="brand__mark" aria-hidden="true">
            <Icon name="mic" size={18} />
          </span>
          <span className="brand__name">Transkripcija</span>
        </Link>
        <div className="topbar__right">
          <SyncIndicator status={status} />
          {!minimal &&
            (user ? (
              <AccountMenu user={user} />
            ) : (
              <Link to={{ name: 'settings' }} className="icon-btn" aria-label="Nastavitve" title="Nastavitve">
                <Icon name="settings" />
              </Link>
            ))}
        </div>
      </div>
    </header>
  );
}
